import { useState, useEffect } from "react";
import { useForm } from "@inertiajs/react";
import { Heading, Box, Flex, Text, Button, Checkbox } from "@radix-ui/themes";

interface ColumnAnalyzerProps {
  sheetId: number;
  tabId: number;
  columns: string[];
}

export function ColumnAnalyzer({ sheetId, tabId, columns }: ColumnAnalyzerProps) {
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
    new Set()
  );

  const form = useForm({
    tab_id: tabId,
    columns: [] as string[],
  });

  // Update tab_id when it changes (user switches tabs)
  useEffect(() => {
    form.setData("tab_id", tabId);
    // Clear column selection when switching tabs
    setSelectedColumns(new Set());
  }, [tabId]);

  const toggleColumn = (column: string) => {
    const newSelected = new Set(selectedColumns);
    if (newSelected.has(column)) {
      newSelected.delete(column);
    } else {
      newSelected.add(column);
    }
    setSelectedColumns(newSelected);
  };

  const handleAnalyze = () => {
    const columnsArray = Array.from(selectedColumns);

    // Transform the form data before submitting
    form.transform((data) => ({
      ...data,
      tab_id: tabId,
      columns: columnsArray,
    }));

    // Submit the form
    form.post(`/sheets/${sheetId}/analyze`, {
      onSuccess: () => {
        // Clear selection after successful submission
        setSelectedColumns(new Set());
      },
    });
  };

  return (
    <Box mt="8">
      <Heading size="5" mb="2">
        Analyze Columns
      </Heading>
      <Text
        size="2"
        style={{
          color: "var(--gray-11)",
          display: "block",
          marginBottom: "var(--space-4)",
        }}
      >
        Select text columns to automatically group similar responses into
        categories for easier analysis.
      </Text>

      {columns.length > 0 ? (
        <Flex direction="column" gap="6">
          <Flex direction="column" gap="3">
            {columns.map((column, index) => (
              <Box key={index} asChild>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-3)",
                    padding: "var(--space-3)",
                    border: "1px solid var(--gray-a5)",
                    borderRadius: "var(--radius-2)",
                    cursor: "pointer",
                    backgroundColor: selectedColumns.has(column)
                      ? "var(--gray-a2)"
                      : "transparent",
                  }}
                  className="hover:opacity-90"
                >
                  <Checkbox
                    checked={selectedColumns.has(column)}
                    onCheckedChange={() => toggleColumn(column)}
                  />
                  <Text weight="medium">{column}</Text>
                </label>
              </Box>
            ))}
          </Flex>

          <Flex align="center" gap="4">
            <Button
              variant="classic"
              size="3"
              onClick={handleAnalyze}
              disabled={selectedColumns.size === 0 || form.processing}
            >
              {form.processing ? "Starting Analysis..." : "Analyze Selected Columns"}
            </Button>
            {selectedColumns.size > 0 && (
              <Text size="2" style={{ color: "var(--gray-11)" }}>
                {selectedColumns.size} column
                {selectedColumns.size !== 1 ? "s" : ""} selected
              </Text>
            )}
          </Flex>
        </Flex>
      ) : (
        <Text style={{ color: "var(--gray-11)" }}>
          No columns available in this tab.
        </Text>
      )}
    </Box>
  );
}
